// The master switch for the whole partner API.
//
// It lives here, at the top of the key screen, rather than in its own settings
// page: issuing a key while the API is off produces a credential that works
// against nothing, and the only thing that would have told you is a switch
// buried two menus away.
//
// Fail-closed, matching the server rule exactly (services/partner_api_policy.py):
// a missing row, an unreadable row, or any shape other than an explicit `true`
// all mean OFF. A screen that said "on" while the server behaved as "off" would
// be the worst kind of false confidence.

import { useAdminBooleanConfig } from '../../api/admin-config-switch'
import { useT } from '../../i18n'
import { partnerApiI18n } from '../../i18n/admin/partner-api'

const CONFIG_KEY = 'partner_api.enabled'

export function PartnerApiSwitch({
  activeKeyCount,
  sharedProductCount,
}: {
  activeKeyCount: number
  sharedProductCount: number
}) {
  const t = useT(partnerApiI18n).switch
  const { enabled, readFailed, setEnabled, isSaving, writeFailed } =
    useAdminBooleanConfig(CONFIG_KEY)

  // Three things must all be true before a partner can read anything. Saying
  // which one is missing beats a switch that is on and a partner who still sees
  // an empty list.
  const blockers: string[] = []
  if (!enabled) blockers.push(t.blockerSwitchOff)
  if (activeKeyCount === 0) blockers.push(t.blockerNoKey)
  if (sharedProductCount === 0) blockers.push(t.blockerNoProducts)

  return (
    <section
      className="rounded border border-slate-200 p-4"
      data-testid="partner-api-switch"
    >
      <h3 className="text-base font-semibold text-slate-900">{t.title}</h3>
      <p className="mt-1 text-sm text-slate-500">
        {t.description}
      </p>

      <label className="mt-4 flex items-center gap-3">
        <input
          type="checkbox"
          role="switch"
          checked={enabled}
          disabled={isSaving}
          onChange={(e) => setEnabled(e.target.checked)}
          className="h-5 w-5 cursor-pointer"
        />
        <span className="text-sm font-medium text-slate-800">
          {t.toggleLabel}
        </span>
        <span
          className={`rounded px-2 py-0.5 text-[11px] ${
            enabled
              ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'
              : 'bg-slate-100 text-slate-500'
          }`}
        >
          {enabled ? t.on : t.off}
        </span>
      </label>

      {blockers.length > 0 && (
        <p className="mt-3 rounded bg-slate-50 px-3 py-2 text-xs text-slate-600" role="status">
          {t.blocked(blockers)}
        </p>
      )}

      {enabled && blockers.length === 0 && (
        <p className="mt-3 rounded bg-emerald-50 px-3 py-2 text-xs text-emerald-800" role="status">
          {t.live}
        </p>
      )}

      {readFailed && (
        <p className="mt-2 text-xs text-rose-600" role="status">
          {t.readFailed}
        </p>
      )}
      {writeFailed && (
        <p className="mt-2 text-xs text-rose-600" role="alert">
          {t.writeFailed}
        </p>
      )}
    </section>
  )
}
